import memoizeOne from 'memoize-one';
import type { IntlShape } from '@atlassian/jira-intl';
import { transformCascadingSelectField } from '../field-types/cascading-select/transform';
import { DEV_SUMMARY_FIELD_KEY, transformDevSummaryField } from '../field-types/dev-summary';
import { transformGoalsField } from '../field-types/goals';
import { transformGroupField } from '../field-types/group/transform';
import { transformParentField } from '../field-types/parent/transform';
import { transformProjectField } from '../field-types/project/transform';
import { transformRadioSelectField } from '../field-types/radio-select/transform';
import { transformResolutionField } from '../field-types/resolution/transform';
import { transformStatusCategoryField } from '../field-types/status-category/transform';
import { transformTeamField } from '../field-types/team/transform';
import { transformVersionField } from '../field-types/version/transform';
import { type jiraBusinessFields, isJiraBusinessFields } from '../types';
import type { IssueNode } from './types';

// Used for list view controller when transforming Gira response
const createSupportedTransformations = memoizeOne(() => ({
	...transformVersionField(),
	...transformParentField(),
	...transformTeamField(),
	...transformGoalsField(),
	...transformResolutionField(),
	...transformGroupField(),
	...transformRadioSelectField(),
	...transformStatusCategoryField(),
	...transformProjectField(),
	...transformCascadingSelectField(),
}));

type SupportedTransformations = ReturnType<typeof createSupportedTransformations>;

const isValidTypename = (typename: unknown): typename is keyof SupportedTransformations => true;
export const isTransformedFieldPredicate = (field: unknown): field is SupportedTransformations =>
	true;

export const transformField = (field: jiraBusinessFields | null, intl: IntlShape) => {
	const supportedTransformations = createSupportedTransformations();

	if (field == null) {
		return null;
	}
	const typename = field.__typename;

	if (!isValidTypename(typename)) {
		return null;
	}

	const transform = supportedTransformations[typename];

	if (transform == null) {
		return null;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return transform(field as never, intl);
};

export const isTransformFieldSupported = (field: unknown): field is jiraBusinessFields => {
	const supportedTransformations = createSupportedTransformations();

	if (!isJiraBusinessFields(field)) {
		return false;
	}

	if (!isValidTypename(field.__typename)) {
		return false;
	}
	return supportedTransformations[field.__typename] != null;
};

// top level field
export const transformTopLevelFields = (issueNode?: IssueNode | null) =>
	issueNode
		? {
				[DEV_SUMMARY_FIELD_KEY]: transformDevSummaryField(issueNode.devSummaryCache),
			}
		: {};
